/* RESPONSIBLE TEAM: team-inbox */
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import storage from 'embercom/vendor/intercom/storage';
import type Session from 'embercom/services/session';
import type TeammateAiTranslationSettings from 'embercom/services/teammate-ai-translation-settings';
import type LanguagesService from 'embercom/services/languages-service';
import type CopilotUsageService from 'embercom/services/copilot-usage-service';
import type Conversation from 'embercom/objects/inbox/conversation';

export type TranslateContentReason =
  | 'enabled'
  | 'workspace_disabled'
  | 'teammate_disabled'
  | 'no_copilot_access'
  | 'quota_exceeded'
  | 'manually_disabled'
  | 'excluded_language'
  | 'conversation_not_found';

export interface TranslationResult {
  is_enabled: boolean;
  reason: TranslateContentReason;
}

export interface AccessResult {
  hasAccess: boolean;
  reason: TranslateContentReason;
}

export default class ConversationTranslationSettings extends Service {
  @service declare session: Session;
  @service declare intercomEventService: $TSFixMe;
  @service declare teammateAiTranslationSettings: TeammateAiTranslationSettings;
  @service declare languagesService: LanguagesService;
  @service declare copilotUsageService: CopilotUsageService;

  @tracked private translationDisabledConversations: Set<string> = new Set();

  constructor() {
    super(...arguments);
    this.translationDisabledConversations = new Set(this.disabledConversationIds());
  }

  get autoTranslationEnabled() {
    return (
      this.session.workspace.aiInboxTranslationsEnabled &&
      this.teammateAiTranslationSettings.isInboxTranslationEnabled
    );
  }

  autoTranslationEnabledForConversation(id: string | number | undefined): boolean {
    if (!this.autoTranslationEnabled) {
      return false;
    }

    if (!id) {
      return false;
    }

    return !this.translationDisabledConversations.has(String(id));
  }

  autoTranslationEnabledForConversationObject(conversation: Conversation | undefined): boolean {
    if (!this.session.workspace.isFeatureEnabled('multilingual-ai-translation-pricing')) {
      return this.autoTranslationEnabledForConversation(conversation?.id);
    }

    if (!conversation) {
      return false;
    }

    // Check basic workspace and teammate settings
    if (!this.autoTranslationEnabled) {
      return false;
    }

    if (!this.hasTranslationAccessForConversation(conversation)) {
      return false;
    }

    if (this.isTranslationManuallyDisabledForConversation(conversation.id)) {
      return false;
    }

    let conversationLanguage = conversation.getAttributeForDescriptor('language')?.value;

    if (
      typeof conversationLanguage === 'string' &&
      this.teammateAiTranslationSettings.excludedLanguages.includes(conversationLanguage)
    ) {
      return false;
    }

    return true;
  }

  getTranslationResultForConversationObject(
    conversation: Conversation | undefined,
  ): TranslationResult {
    if (!this.session.workspace.isFeatureEnabled('multilingual-ai-translation-pricing')) {
      let is_enabled = this.autoTranslationEnabledForConversation(conversation?.id);
      return {
        is_enabled,
        reason: is_enabled ? 'enabled' : 'manually_disabled',
      };
    }

    if (!conversation) {
      return { is_enabled: false, reason: 'conversation_not_found' };
    }

    if (!this.session.workspace.aiInboxTranslationsEnabled) {
      return { is_enabled: false, reason: 'workspace_disabled' };
    }

    if (!this.teammateAiTranslationSettings.isInboxTranslationEnabled) {
      return { is_enabled: false, reason: 'teammate_disabled' };
    }

    // Check copilot access
    let accessResult = this.getTranslationAccessForConversation(conversation);
    if (!accessResult.hasAccess) {
      return { is_enabled: false, reason: accessResult.reason };
    }

    // Check if manually disabled for this conversation
    if (this.isTranslationManuallyDisabledForConversation(conversation.id)) {
      return { is_enabled: false, reason: 'manually_disabled' };
    }

    // Check excluded languages
    let conversationLanguage = conversation.getAttributeForDescriptor('language')?.value;
    if (
      typeof conversationLanguage === 'string' &&
      this.teammateAiTranslationSettings.excludedLanguages.includes(conversationLanguage)
    ) {
      return { is_enabled: false, reason: 'excluded_language' };
    }

    return { is_enabled: true, reason: 'enabled' };
  }

  @action toggleAutoTranslationEnabledForConversation(id: string | number): void {
    let isEnabled = this.autoTranslationEnabledForConversation(id);
    if (isEnabled) {
      this.addIdToDisabledList(String(id));
    } else {
      this.removeIdFromDisabledList(String(id));
    }
    isEnabled = !isEnabled;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'translation_settings',
      section: 'admin_details',
      context: 'conversation_translation_settings',
      metadata: {
        is_translation_enabled: isEnabled,
        conversation_id: id,
      },
    });
  }

  isLanguageInExcludedList(language: string) {
    for (let localeId of this.teammateAiTranslationSettings.excludedLanguages) {
      if (this.languagesService.getLanguageName(localeId) === language) {
        return true;
      }
    }
    return false;
  }

  hasTranslationAccessForConversation(conversation?: any): boolean {
    return this.getTranslationAccessForConversation(conversation).hasAccess;
  }

  getTranslationAccessForConversation(conversation?: any): AccessResult {
    // Users with no copilot access are completely blocked
    if (!this.session.teammate.hasCopilotAccess) {
      return { hasAccess: false, reason: 'no_copilot_access' };
    }

    // Check copilot access levels
    let hasUnlimitedAccess = this.session.teammate.hasUnlimitedCopilotAccess;
    if (hasUnlimitedAccess) {
      return { hasAccess: true, reason: 'enabled' };
    }

    // For limited copilot users, check specific scenarios
    if (this.session.teammate.hasLimitedCopilotAccess) {
      // If conversation is already translated, allow access
      if (conversation) {
        let conversationIsAlreadyTranslated =
          conversation.getAttributeForDescriptor?.('auto_translation_applied')?.value === true;
        if (conversationIsAlreadyTranslated) {
          return { hasAccess: true, reason: 'enabled' };
        }
      }

      // For new translations, check if they have quota left
      let hasTranslationsLeft = this.copilotUsageService.hasTranslationsLeft(
        this.session.teammate,
        conversation,
      );
      return {
        hasAccess: hasTranslationsLeft,
        reason: hasTranslationsLeft ? 'enabled' : 'quota_exceeded',
      };
    }

    // Fallback - no access
    return { hasAccess: false, reason: 'no_copilot_access' };
  }

  private isTranslationManuallyDisabledForConversation(id: string | number | undefined): boolean {
    if (!id) {
      return false;
    }

    return this.translationDisabledConversations.has(String(id));
  }

  private disabledConversationIds(): string[] {
    let ids = storage.get(this.translationDisabledConversationIdsKey());
    return ids ? ids.split(',') : [];
  }

  private addIdToDisabledList(id: string) {
    let ids = this.disabledConversationIds();
    ids.push(`${id}`);
    storage.set(this.translationDisabledConversationIdsKey(), ids.join(','));

    this.translationDisabledConversations = new Set(this.disabledConversationIds());
  }

  private removeIdFromDisabledList(id: string) {
    let ids = this.disabledConversationIds();
    ids = ids.filter((disabledId) => disabledId !== `${id}`);
    storage.set(this.translationDisabledConversationIdsKey(), ids.join(','));
    this.translationDisabledConversations = new Set(this.disabledConversationIds());
  }

  private translationDisabledConversationIdsKey() {
    let appId = this.session.workspace.id;
    let adminId = this.session.teammate.id;
    return `${appId}::${adminId}::ConversationTranslationSettings::translationDisabled`;
  }
}

declare module '@ember/service' {
  interface Registry {
    conversationTranslationSettings: ConversationTranslationSettings;
    'conversation-translation-settings': ConversationTranslationSettings;
  }
}
